// DataGrid — domain-neutral drill-down grid.
// Props:
//   rows: Row[]                        — data rows
//   columns: ColDef[]                  — { key, label, width, align?, render?, editable? (bool | row => bool), editType?, options?, addField? }
//   editType?: string | (row) => string — row-aware inline-edit type. A function is
//                                          resolved PER ROW at render time (row-aware
//                                          override, e.g. boolean params inline-edit as
//                                          a checkbox while others stay number/text); a
//                                          string behaves exactly as before (unchanged).
//   options?: array | (row) => array   — S1b: for editType 'select', options may also
//                                          be a function resolved PER ROW (e.g. a list
//                                          param's Typical dropdown differs by its own
//                                          Size Unit); a plain array behaves as before.
//   editValue?: (row) => value          — S2.2: row-aware edit-buffer seed/read value, same
//                                          additive pattern as editType/options. When present,
//                                          the buffer for that cell is seeded from
//                                          editValue(row) instead of row[col.key] — lets a
//                                          column's edit control read/write a DIFFERENT field
//                                          than its read-mode `key` (e.g. a list param's
//                                          Typical column keys on typical_value for read/save
//                                          plumbing, but its select must be seeded from
//                                          typical_text — the field its options are keyed
//                                          by — or the closed select can't match an option
//                                          and falls back to showing the raw key value).
//                                          Absent (default) = exactly row[col.key], today's
//                                          behavior, byte-for-byte for every other column.
//   onRowClick?: (row) => void         — single-click: SELECT the row (no drill)
//   onRowDoubleClick?: (row) => void   — double-click: DRILL into children (brief §1)
//   selectedRowId?: string | number | null
//   loading?: boolean
//   emptyMessage?: string
//   onSave?: (rowId, field, value) => void          — inline cell edit; ONLY called on explicit Save (brief §3)
//   col.render?: (row, ctx) => ReactNode            — ctx = { inEdit, buffer(rowId, colKey, value), buffered }.
//                                                     A column rendering its own editor MUST stage through
//                                                     ctx.buffer, not write straight to the server — otherwise
//                                                     the row auto-saves on click and Cancel cannot undo it.
//                                                     ctx.buffer accepts ANY colKey, so one control may stage
//                                                     several fields (a cascade setting name + pointer).
//   col.textKind?: string                           — the column's text style, a row of Text's KINDS
//                                                     ('small', 'code', …; none = body text). Every
//                                                     edit control in the cell takes it (cellStyles.js
//                                                     cellFont). Declare it when the read renderer
//                                                     draws its value as <Text kind> of that name,
//                                                     or Edit mode will not match read.
//   col.renderAddCell?: (ctx) => ReactNode          — the ADD row's cell for this column, when a plain
//                                                     text input will not do (a cascade, a picker). Same
//                                                     idea as col.render, for the row that does not exist
//                                                     yet: ctx = { fields, set(colKey, value), commit,
//                                                     cancel }. Without it, a grid whose add needs a
//                                                     non-text control has to fall back to renderAddForm,
//                                                     which is a free-floating form that lines up with
//                                                     nothing — Alex, repeatedly: "Fields are not in line
//                                                     with columns." One control may set SEVERAL fields
//                                                     (picking a product can prefill the description).
//   onAdd?: (fields) => Promise<void>               — add row form (addField columns)
//   renderAddForm?: (close: () => void) => ReactNode — library-lane alternative to onAdd:
//                                                      when provided (and onAdd is NOT),
//                                                      DataGrid renders a "+ {addFormLabel}"
//                                                      ghost last row; clicking it opens the
//                                                      consumer's own form in a full-width
//                                                      sub-row in its place (colSpan all
//                                                      columns, same selected-row background).
//                                                      DataGrid owns the open/closed state and
//                                                      passes `close` to the consumer.
//   addFormLabel?: string                            — ghost-row label text (default "Add row")
//   onDelete?: (row) => void|Promise<void>          — delete row button (only when provided). WS1a-i:
//                                                      if the returned promise REJECTS with a message
//                                                      (server guard, e.g. "in use"), the grid shows it
//                                                      INLINE (⚠ + text, dismissible) — no alert popup.
//   onCopy?: (row) => void|Promise<void>            — WS1b: copy/duplicate row button (only when
//                                                      provided), rendered next to delete. Renders a
//                                                      ⧉ icon + "Copy" tooltip (color-blind safe — icon
//                                                      + text, no color meaning). stopPropagation so it
//                                                      never triggers row-click / editOnRowClick.
//   onDeleteCheck?: (row) => string|null            — called before delete confirm; return a string to show a
//                                                      warning message instead of the Delete/Cancel confirm;
//                                                      return null to proceed with normal confirm.
//   onReorder?: (rowId, targetIndex) => void        — reorder column (contract v16). targetIndex is the
//                                                      0-based position within the currently-rendered sibling
//                                                      rows. Driven by a drag grip (⠿, native HTML5 drag).
//                                                      Consumer persists order (POST { toIndex }). Requires the
//                                                      rendered rows to be contiguous same-scope siblings.
//   onEditRow?: (rowId|null) => void                — called when user enters/exits edit mode
//   editRowId?: string|number|null                  — controlled: which row is currently in edit mode
//   editOnRowClick?: boolean                         — WS1a-i opt-in: clicking a data row enters edit
//                                                      mode (reusing onEditRow/editRowId lifecycle) and
//                                                      HIDES the ✎ column. Falsy = today's ✎ behavior.
//                                                      Enable ONLY where row-click has no selection/
//                                                      detail/navigation role. Interactive children
//                                                      stopPropagation; derived cells stay static; a
//                                                      dirty open row blocks click-to-edit elsewhere.
//   footerRow?: Record<colKey, ReactNode>            — summary row rendered in <tfoot>; keys map to column keys
//   storageKey?: string                             — enables col-resize with localStorage persistence (key prefix)
//   multiSelect?: boolean                           — opt-in bulk selection (Spike_BulkActions). Plain click
//                                                      selects ONE row and still falls through to onRowClick
//                                                      (unchanged single-select side effects); Shift-click
//                                                      selects the inclusive range from the last-clicked
//                                                      anchor; Ctrl/Cmd-click toggles one row in/out. Esc or
//                                                      the bar's Clear button clears. While >=1 row is
//                                                      selected, an action bar renders above the table:
//                                                      "N selected · ✕ Delete · ↑ Move up · ↓ Move down ·
//                                                      ⧉ Copy · Clear" — reuses the SAME onDelete (+
//                                                      onDeleteCheck guard) / onReorder / onCopy handlers
//                                                      already wired for the single-row icon columns, looped
//                                                      over the selection (no batch endpoint, no new consumer
//                                                      props). Selected rows get the same --th-row-selected
//                                                      background + left-border marker as selectedRowId — a
//                                                      non-color-only cue.
//
// Edit model (brief §3 — BUFFERED Save/Cancel):
//   Clicking a data row enters edit mode for it.
//   ALL cell edits are buffered locally in `rowBuffer` state.
//   Edits are only committed (onSave called per changed cell) when the user clicks SAVE.
//   CANCEL reverts the buffer — no changes are persisted.
//   Save + Cancel render in a full-width sub-row directly UNDER the edited row
//   (UX Consistency 2), uniform for every grid.
//
// ⚠ THIS USED TO SAY "clicking the ✎ icon enters edit mode", AND THAT HAS NOT BEEN
// TRUE ANYWHERE FOR SOME TIME. The pencil column renders only when
// `onEditRow && !editOnRowClick`, and a sweep on 2026-08-27 found that condition is
// never met: all 8 grids passing onEditRow also pass editOnRowClick, every one of
// them bare (= true), and no HeaderRow caller overrides its `editOnRowClick = true`
// default. So the ✎ column, and everything `showEditCol` guards, is dead code.
//
// Left in place deliberately rather than ripped out mid-session: `showEditCol`
// threads through nine sites including a whole <td> branch, and removing it is
// surgery on the app's most central component for zero visible gain. Filed as
// DS-10. What was actually costing something was this comment, which sent every
// reader looking for a control that does not exist — so the comment is fixed now
// and the code waits for a session that can prove the removal.
//
// Status glyph (brief §2):
//   Import StatusGlyph from TreeNav for use in column render fns.

import { useState, useEffect, useRef, Fragment } from 'react';
import { StatusGlyph } from './TreeNav.jsx';
import { WARN, LOCKED } from '../icons.js';
import TextCell from './TextCell.jsx';
import SelectCell from './SelectCell.jsx';
import CheckboxCell from './CheckboxCell.jsx';
import FormulaCell from './FormulaCell.jsx';
import { cellFont, CELL_FONT } from './cellStyles.js';
import Notice from './Notice.jsx';
import Text from './Text.jsx';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE, BTN_GLYPH } from '../theme-tokens.js';

// Re-export StatusGlyph for backward compat (was StatusRollup before)
export { StatusGlyph };
// Also export the legacy name so existing column render fns still compile
export { StatusGlyph as StatusRollup };

const INPUT_STYLE = {
  width: '100%', boxSizing: 'border-box',
  ...CELL_FONT, border: '1px solid var(--th-accent)',
  padding: 'var(--th-cell-inset, 3px 7px)', outline: 'none', background: 'var(--th-row-even)',
  lineHeight: 'var(--th-cell-line-h, 1.15)',
};

// PADDING IN THE CELL, NOT AROUND IT (Alex, 2026-08-03).
// A cell holding a full-width control must give it the WHOLE cell: an input at
// width:100% only fills the td's content box, so cell padding pushes its border
// inward and it floats with a gap all round -- the thing he pointed at. The inset
// is not lost, it moves inside the control (--th-cell-inset), so its text still
// lines up with the text of an ordinary read cell.
//
// Decided from the RENDERED CONTENT, not a column flag: the two catalog columns
// that need it (size, material) return a plain <span> in read mode and a SelectCell
// in edit mode from the SAME render fn, so nothing static can describe them.
// A checkbox is content, not a fill control -- it keeps the cell's padding.
const FILL_CELL_TYPES = new Set([TextCell, SelectCell, FormulaCell]);
function fillsCell(node) {
  if (!node || typeof node !== 'object' || !node.type) return false;
  if (FILL_CELL_TYPES.has(node.type)) return true;
  if (node.type === 'input') return node.props?.type !== 'checkbox';
  return node.type === 'select' || node.type === 'textarea';
}

// F3 (Alex 2026-07-03): one shared width for all icon columns (copy/delete/
// reorder/edit) — minimum that fits a single glyph. Not resizable (unchanged).
const ICON_COL_WIDTH = 24;

// S3e — stable blank row handed to the in-cell ADD renderers (addInlineEditRender).
// Frozen so a consumer editor can't mutate it; the add editors read their state from
// the consumer's own editor instance, not from this row (a derived cell just sees blanks).
const EMPTY_ADD_ROW = Object.freeze({});

// Row-aware editability (additive, 2026-10-11): `editable` may be a boolean
// (unchanged) or a function (row) => boolean, the same way `editType` and
// `options` already resolve per row. A function that says false wins over an
// editType, so one column can be editable on some rows and read-only on others
// (Catalog: a multi-size family row edits Tag and Label only — Alex: "the tag,
// label must be editable in the main row vs. the size row").
// A row-aware editType that resolves to nothing for a row likewise leaves that
// cell read-only on that row (before, it seeded the edit buffer and then drew
// the read view anyway — same screen, one less unchanged value buffered).
function cellIsEditable(col, row) {
  if (typeof col.editable === 'function') return !!col.editable(row);
  if (col.editable) return true;
  if (typeof col.editType === 'function') return !!col.editType(row);
  return !!col.editType;
}

export default function DataGrid({
  rows,
  columns,
  onRowClick,
  onRowDoubleClick,   // (row) => void — double-click drills into children (brief §1)
  selectedRowId,
  loading,
  emptyMessage,
  onSave,
  onAdd,
  renderAddForm, // (close: () => void) => ReactNode — library-lane ghost-row add form (used only when onAdd is not provided)
  addFormLabel,  // string? — ghost-row label; default "Add row"
  onDelete,
  onCopy,         // (row) => void|Promise<void> — WS1b: copy/duplicate row button, rendered next to delete
  onDeleteCheck,  // (row) => string|null — return warning text to show instead of Delete/Cancel confirm
  deleteImpact,   // (row) => string|null — optional: what ELSE the delete removes, said in the
                  //   confirm after "Delete X?" (Catalog: a family row deletes every size — 2026-10-11).
  deleteBlockedIcon, // (row) => ReactNode|null — when it returns a node, that row shows the node
                  //   INSTEAD of the ✕, and cannot open the delete guard at all. For rows something
                  //   else owns (Alex, 2026-08-03, on Onshape-synced components: "the Onshape icon
                  //   instead of delete"). onDeleteCheck already REFUSES those deletes; this stops the
                  //   grid OFFERING the action, and says whose row it is in the space the offer used
                  //   to occupy. The node carries its own `title` — this is a render path, so it must
                  //   never call onDeleteCheck (which may return a Promise).
  onRowSelect,    // (row) => void — called on EVERY plain row click, whatever else the click does
                  //   (opens edit, hits a locked row, or neither). For a screen whose OTHER cards
                  //   follow "the row you are on" (Catalog: the family's 3D Model and Sizes —
                  //   Alex, 2026-09-30: "it is odd to edit a row and to see a wrong 3D model").
                  //   Not onRowClick: with editOnRowClick that one never fires, by design.
  rowLocked,      // (row) => string|null — INF-14. A string LOCKS that row and is the reason, shown on
                  //   hover: the row does not open for edit, its ✕ becomes a 🔒 and bulk delete
                  //   skips it with the reason. Copy stays — copying is how a locked row is used. The
                  //   rule of WHICH rows lock lives in one place (src/web/can-change.js), not here.
  onReorder,      // (rowId, targetIndex) => void — 0-based within rendered rows (contract v16); consumer persists order
  onEditRow,      // (rowId|null) => void — consumer controls which row is in edit mode
  editRowId,      // string|number|null — which row is currently in edit mode (controlled)
  editOnRowClick, // boolean — clicking a data row enters edit mode (reusing the
                  //   onEditRow/editRowId lifecycle) and HIDES the ✎ icon column.
                  //   ⚠ NOMINALLY opt-in, ACTUALLY universal: every caller sets it, so
                  //   the falsy branch it guards is unreachable (see the note above).
                  //   Only meaningful where row-click has NO selection/detail/navigation
                  //   role (see Component_Contract) — which is now everywhere.
  footerRow,      // Record<colKey, ReactNode>? — summary row in <tfoot>
  getRowStyle,    // (row) => CSSProperties? — optional per-row style override
  renderSubRow,   // (row) => ReactNode — renders a full-width sub-row under the selected row
  renderExpandedRow, // UX-16 — (row, { offsetOf }) => ReactNode. A full-width sub-row under EVERY row the
                  //   `offsetOf(colKey)` gives the px from the sub-row's left edge to that
                  //   column's left edge, so content can line up UNDER a specific column
                  //   (UX-17.1) and stay lined up when the user resizes columns — a
                  //   hardcoded indent silently drifts the first time a width changes.
                  //   consumer lists in `expandedRowIds`. Deliberately separate from
                  //   renderSubRow, which follows the SELECTED row only: expansion is a
                  //   disclosure (many rows at once, survives selection changes), selection
                  //   is not. Consumer owns the open set and its toggle control.
  expandedRowIds, // Set<string>|null — row ids (as String) whose renderExpandedRow is shown
                  // ERR-1(b) — there is deliberately NO rowError prop. The grid catches a
                  // rejected onSave itself and renders the reason under that row. A prop
                  // the consumer had to pass is a prop a consumer forgets: ProductsView and
                  // CatalogView were fixed one at a time, a week apart, for the same bug.
                  // What onSave must do: THROW (or reject) on failure. Returning quietly
                  // means success, and the row leaves edit mode.
  editWarnings,   // (row, buffered) => string[] — NON-BLOCKING warnings shown beside Save while a
                  //   row is in edit mode. Alex 2026-07-31, on picking a material nothing stocks:
                  //   "Should the warning also goes beside the save button as other standard warning."
                  //   It deliberately does NOT disable Save: 17 materials have no stocked source
                  //   today, so blocking would make them unselectable mid-cleanup, and the agreed
                  //   behaviour is that a missing source WARNS and prices visibly wrong rather than
                  //   refusing the edit. `buffered` is the row's staged values merged over the row.
  stickyCols,     // number? — how many LEADING data columns stay frozen when the grid is
                  //   scrolled sideways (UX-7). Default 1 = today's behaviour (column 0 has
                  //   always been sticky). Requires the one-line cells of UX-8 to be safe.
  storageKey,     // string? — enables column resize; widths persisted to localStorage
  multiSelect,    // boolean? — opt-in bulk selection (Spike_BulkActions) — see header doc above
  // toolbar — content pinned into the LEFT of the bulk-action bar (Alex, 2026-08-19,
  // annotating the catalog: put the status chips and Copy shelf into that same row).
  // ⚠ SUPPLYING IT ALSO PINS THE BAR'S HEIGHT. At rest the bar used to render nothing,
  // so the first row-selection INSERTED a bar and pushed every row down — the same
  // complaint as the LINE block (UX-35). With a toolbar the bar is always present and
  // only its contents change, so selecting moves nothing. Grids that pass no toolbar
  // keep the old appear-on-selection behaviour, so this changes no other screen.
  toolbar,
  addInlineEditRender, // S3e — { [colKey]: (row) => ReactNode }. When the renderAddForm ghost row
                  //   is OPEN and this is provided, the add form renders IN-CELL: a blank/ghost row
                  //   whose columns each render this map's editor (reusing the SAME per-column
                  //   editRender pattern as inline EDIT), instead of one full-width renderAddForm
                  //   sub-row. Columns without an entry render an empty cell. The blank row passed
                  //   to each renderer is a frozen {} (EMPTY_ADD_ROW). Absent → today's full-width
                  //   renderAddForm behavior, unchanged.
  renderAddSubRow, // S3e — (close) => ReactNode. The extension sub-row rendered UNDER the in-cell
                  //   add row (typically the consumer's param mapping + Add/Cancel bar). Receives the
                  //   same `close` as renderAddForm. Used together with addInlineEditRender.
  onAddOpenChange, // S3e — (open: boolean) => void. Fired when the "+ {addFormLabel}" ghost row opens
                  //   (true) or closes (false), so a consumer with a persistent add editor can reset
                  //   it (e.g. bump a nonce) for a fresh add each open. Optional.
  inlineEditRowId, // string|number|null — S3d: the row currently being edited IN ITS OWN CELLS.
                  //   For each column that defines `editRender(row) => ReactNode`, that row's cell
                  //   renders the consumer's editor content instead of the read-mode value — so the
                  //   editors ARE the grid cells and track column-resize. Additive + independent of
                  //   the buffered onEditRow/rowBuffer/onSave lifecycle (this path never touches the
                  //   buffer or calls onSave — the consumer owns its own save, typically in a
                  //   renderSubRow extension). Columns WITHOUT editRender keep their normal render for
                  //   the edited row. Absent (default) = today's behavior, byte-for-byte.
                  //   col.editColSpan?: number (2026-08-21) — a COLUMN property (not a DataGrid prop),
                  //   for a column that also defines editRender (inline EDIT) and/or is keyed in
                  //   addInlineEditRender (in-cell ADD). When >1, that column's <td> spans the next
                  //   N-1 columns TOO, for that one row only — those columns' own <td>s are skipped
                  //   entirely, not rendered-empty-and-covered. Real estate reclaimed, not painted
                  //   over: `overflow:visible` alone (already set on these cells, for the search-
                  //   results popover) only lets content spill past its own <td> visually; it does
                  //   not stop the NEXT column's <td> from painting its own content on top of that
                  //   spill. First consumer: ProductsView ComponentsGrid's Ref cell (Source+L1▸L2▸L3▸
                  //   item+search) reclaiming the Label cell's width while picking — Label has
                  //   nothing useful to show mid-pick anyway (S3j). Columns that never set
                  //   editColSpan behave byte-identically — untouched columns, and every other
                  //   consumer, are unaffected.
                  //   col.resizable?: boolean (2026-08-22) — a COLUMN property. false hides that
                  //   column's resize handle, the same "fixed width, not draggable" treatment the
                  //   copy/delete/reorder/edit icon columns already have. Default true (today's
                  //   behavior, unchanged) — the "#" row-number column is the one consumer, so it
                  //   reads as a fifth icon-lane column instead of a resizable data column (Alex:
                  //   "it behaves like the edit, like the copy or reorder or delete... same, not
                  //   resizable").
}) {
  // §3 — Buffered edit state: rowId → { colKey → bufferedValue }
  const [rowBuffer, setRowBuffer] = useState({}); // { [rowId]: { [colKey]: value } }

  const [addActive, setAddActive] = useState(false);
  const [addFields, setAddFields] = useState({});
  const [addError, setAddError] = useState(null); // inline ⚠ under the add row (no alert popups)
  // renderAddForm ghost row (library lane) — DataGrid owns only the open/closed toggle;
  // the consumer's form owns its own field state via the `close` callback contract.
  //
  // TRIED AND REJECTED (Alex, 2026-08-01): an always-open add row. It put the add
  // editor and a row editor on screen at once -- two Save bars, two live rows --
  // and he was right that it is not acceptable. What he wants instead is ONE
  // gesture and ONE open thing: "I click directly on a row, and then it goes to
  // the add mode, like when I am in another row and it goes in edit mode." Hence
  // the mutual exclusion in openAdd / enterEditMode below, and no always-open flag.
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(null); // rowId being confirmed
  const [deleteWarning, setDeleteWarning] = useState(null); // { rowId, message } — non-deletable warning
  // ERR-1(b) — { rowId, message } when onSave rejects. OWNED HERE, not passed in: a prop
  // the consumer had to remember is a prop some consumer eventually forgets, and two views
  // had already shipped without it. The grid invokes the action, so the grid places the
  // failure — the same way addError and deleteWarning already work.
  const [saveError, setSaveError] = useState(null);

  // Reorder (contract v15) — native HTML5 drag. Source stashed in a ref (not state,
  // to avoid re-render churn during drag). dropTarget drives the 2px drop-indicator line.
  const dragSrcRef = useRef(null); // { rowId, index }
  const tableRef = useRef(null);
  const [dropTarget, setDropTarget] = useState(null); // { index, edge: 'top'|'bottom' }

  // `:v2:` (Alex, 2026-07-27: "can all columns width be reset") — bumping the
  // key retires every width dragged before today in one line, so the grid comes
  // back on the CODE defaults. Widths saved from now on persist as before; the
  // old v1 entries are simply never read again. Bump again to reset again.
  const [colWidths, setColWidths] = useState(() => columns.map(c => c.width));
  const colWidthsRef = useRef(columns.map(c => c.width));

  // Sync widths when storageKey changes (table context switch) — ALSO when the
  // column SET changes shape (columns.length), not just its identity. A consumer
  // can grow `columns` after mount without touching storageKey (CatalogView's
  // experimental Prj Qty/Prj Cost columns, added once an async catalogUsage
  // fetch resolves) — colWidths, seeded once at mount, then had no entry for
  // the new trailing columns; `colWidths[ci]` read undefined, and a `col` with
  // no width in a tableLayout:fixed table lets the browser auto-distribute the
  // leftover space instead, stretching/collapsing whichever columns had no
  // pinned width (Alex, 2026-08-23: a "P" header with a huge empty gap after
  // it — Prj Qty collapsed to its first letter, Prj Cost took the rest). Same
  // reset-to-defaults-or-matching-saved-width logic as a storageKey switch,
  // now also covers a columns array that grew or shrank in place.
  useEffect(() => {
    const defaults = columns.map(c => c.width);
    if (storageKey) {
      const saved = JSON.parse(localStorage.getItem(`inoviu:cols:v2:${storageKey}`) || 'null');
      if (Array.isArray(saved) && saved.length === defaults.length) {
        setColWidths(saved);
        colWidthsRef.current = saved;
        return;
      }
    }
    setColWidths(defaults);
    colWidthsRef.current = defaults;
  }, [storageKey, columns.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear the row buffer when editRowId is cleared externally (e.g. Cancel from outside)
  useEffect(() => {
    if (editRowId == null) {
      setRowBuffer({});
    }
  }, [editRowId]);

  // Bulk multi-select (opt-in via `multiSelect`) — Shift-range / Ctrl-toggle / plain-click-
  // replace. Reuses the SAME onDelete/onCopy/onReorder/onDeleteCheck handlers the single-row
  // icon columns already call, looped over the selection — no new consumer props needed.
  const [bulkSelected, setBulkSelected] = useState(() => new Set()); // Set<string(rowId)>
  const [bulkAnchor, setBulkAnchor] = useState(null); // last-clicked row id (range anchor)
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(null); // { clear, blocked } pending confirm
  const [bulkResult, setBulkResult] = useState(null); // { deletedCount, skipped } — result banner

  // Prune stale ids out of the bulk selection whenever the underlying rows change (e.g. a
  // single-row delete/copy elsewhere removed or shifted a selected row).
  useEffect(() => {
    if (!multiSelect) return;
    setBulkSelected(prev => {
      if (prev.size === 0) return prev;
      const live = new Set((rows || []).map(r => String(r.id)));
      let changed = false;
      const next = new Set();
      prev.forEach(id => { if (live.has(id)) next.add(id); else changed = true; });
      return changed ? next : prev;
    });
  }, [rows, multiSelect]);

  // Esc clears the bulk selection (brief: "Esc or a Clear control clears").
  useEffect(() => {
    if (!multiSelect || bulkSelected.size === 0) return;
    const onKey = e => { if (e.key === 'Escape') clearBulkSelection(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [multiSelect, bulkSelected.size]); // eslint-disable-line react-hooks/exhaustive-deps

  function clearBulkSelection() {
    setBulkSelected(new Set());
    setBulkAnchor(null);
    setBulkDeleteConfirm(null);
    setBulkResult(null);
  }

  // Delete — pre-flight EVERY selected row through onDeleteCheck (the SAME guard the ✕ icon
  // uses), split clear/blocked, THEN confirm (warning always fires before confirm, Contract).
  async function handleBulkDeleteClick() {
    if (!onDelete) return;
    setBulkBusy(true);
    const selectedRows = (rows || []).filter(r => bulkSelected.has(String(r.id)));
    const blocked = [];
    const clear = [];
    for (const row of selectedRows) {
      let warning = rowLocked?.(row) || null;
      if (!warning && onDeleteCheck) {
        warning = onDeleteCheck(row);
        if (warning && typeof warning.then === 'function') {
          try { warning = await warning; } catch { warning = null; }
        }
      }
      if (warning) blocked.push({ row, message: warning });
      else clear.push(row);
    }
    setBulkBusy(false);
    setBulkDeleteConfirm({ clear, blocked });
  }

  async function confirmBulkDelete() {
    if (!bulkDeleteConfirm) return;
    const { clear, blocked } = bulkDeleteConfirm;
    setBulkDeleteConfirm(null);
    setBulkBusy(true);
    let deletedCount = 0;
    const skipped = [...blocked];
    for (const row of clear) {
      try { await onDelete(row); deletedCount++; }
      catch (err) { skipped.push({ row, message: (err && err.message) || 'Delete failed' }); }
    }
    setBulkBusy(false);
    setBulkResult({ deletedCount, skipped });
    setBulkSelected(new Set());
  }

  function cancelBulkDelete() { setBulkDeleteConfirm(null); }

  // Move up/down — reorders the selection AS A BLOCK, confined to `rows` (the current
  // group — same contract as the drag reorder: "contiguous same-scope siblings"). Classic
  // multi-item bubble: walk top->bottom (up) / bottom->top (down); whenever a selected row
  // sits next to an unselected one, swap them one step via the SAME onReorder(rowId,
  // targetIndex) the drag grip uses. Naturally stops at either boundary — once the block
  // abuts it, no more swaps are possible.
  async function handleBulkMove(direction) {
    if (!onReorder) return;
    const ids = (rows || []).map(r => r.id);
    const isSel = id => bulkSelected.has(String(id));
    const working = [...ids];
    const moves = [];
    if (direction === 'up') {
      for (let idx = 1; idx < working.length; idx++) {
        if (isSel(working[idx]) && !isSel(working[idx - 1])) {
          const id = working[idx];
          moves.push({ id, targetIndex: idx - 1 });
          working.splice(idx, 1);
          working.splice(idx - 1, 0, id);
        }
      }
    } else {
      for (let idx = working.length - 2; idx >= 0; idx--) {
        if (isSel(working[idx]) && !isSel(working[idx + 1])) {
          const id = working[idx];
          moves.push({ id, targetIndex: idx + 1 });
          working.splice(idx, 1);
          working.splice(idx + 1, 0, id);
        }
      }
    }
    if (!moves.length) return;
    setBulkBusy(true);
    for (const mv of moves) { await onReorder(mv.id, mv.targetIndex); }
    setBulkBusy(false);
  }

  // Copy — loops the SAME per-row onCopy (tag-uniqueness lives in that handler); sequential
  // awaits so successive tag-uniqueness checks never race.
  async function handleBulkCopy() {
    if (!onCopy) return;
    const selectedRows = (rows || []).filter(r => bulkSelected.has(String(r.id)));
    setBulkBusy(true);
    for (const row of selectedRows) {
      try { await onCopy(row); } catch { /* per-row handler already surfaces its own error */ }
    }
    setBulkBusy(false);
    setBulkSelected(new Set());
  }

  const bulkBtnStyle = {
    // ⚠ COMPACT WHEN A TOOLBAR SHARES THE BAR (Alex, 2026-08-19: "fix this top row
    // height to avoid also rows moving when selecting"). The default edit-button
    // padding made the bar 54px with a selection against 33px at rest, so picking
    // rows still pushed the grid down 21px — the arrangement alone did not fix the
    // movement. Matching the chips' padding makes both states the same height.
    fontSize: 'var(--fs-small)', padding: toolbar ? '2px 8px' : 'var(--tg-editbtn-pad)',
    lineHeight: toolbar ? 1.5 : undefined,
    // ⚠ AND THEY MUST NOT BE SQUEEZED. With the toolbar sharing the row, a narrow
    // window shrank these buttons until their LABELS wrapped ("Move up" over two
    // lines, 56px tall), which grew the bar to 68px — the movement came back by a
    // different route. nowrap + no shrink keeps every button one line; the bar
    // scrolls sideways instead of growing downwards.
    whiteSpace: toolbar ? 'nowrap' : undefined,
    flexShrink: toolbar ? 0 : undefined,
    background: 'var(--tg-cancel-bg)', color: 'var(--tg-cancel-color)',
    border: 'var(--tg-cancel-border)', borderRadius: 'var(--tg-editbtn-radius)',
    cursor: bulkBusy ? 'default' : 'pointer', opacity: bulkBusy ? 0.6 : 1,
  };

  // editOnRowClick hides the ✎ column but keeps the onEditRow/editRowId lifecycle (click is the trigger).
  const showEditCol = onEditRow && !editOnRowClick;
  // F3 (Alex 2026-07-03): all four icon columns share ONE narrow width — the
  // minimum that fits a single glyph — instead of each having its own tuned
  // width. Not resizable (unchanged).
  const copyColWidth = onCopy ? ICON_COL_WIDTH : 0;
  const deleteColWidth = onDelete ? ICON_COL_WIDTH : 0;
  const reorderColWidth = onReorder ? ICON_COL_WIDTH : 0;
  const editColWidth = showEditCol ? ICON_COL_WIDTH : 0; // Save/Cancel render as a sub-row under the edited row
  const totalWidth = colWidths.reduce((s, w) => s + w, 0) + copyColWidth + deleteColWidth + reorderColWidth + editColWidth;
  const extraCols = (onCopy ? 1 : 0) + (onDelete ? 1 : 0) + (onReorder ? 1 : 0) + (showEditCol ? 1 : 0);

  // A4 (overnight batch): the copy/delete/reorder/edit icon columns render at the
  // FAR LEFT of the grid now (previously far right), always in the same place
  // regardless of grid width. They form a sticky-left block (position:sticky,
  // stacked left:0..N by cumulative width), and the first DATA column becomes
  // sticky immediately after them (left = summed icon width) instead of at left:0.
  // Order preserved: copy, delete, reorder, edit.
  const iconColsWidth = copyColWidth + deleteColWidth + reorderColWidth + editColWidth;
  // UX-17.1 — where a column starts, measured from the left edge of an expanded
  // sub-row's <td> (which spans the icon lane too). Reads the LIVE colWidths, so it
  // follows a column resize instead of drifting away from it.
  const expandedOffsetOf = (colKey) => {
    const i = columns.findIndex(c => c.key === colKey);
    if (i < 0) return iconColsWidth;
    return iconColsWidth + colWidths.slice(0, i).reduce((s, w) => s + (w || 0), 0);
  };
  let _iconLeftAcc = 0;
  const copyColLeft = _iconLeftAcc; _iconLeftAcc += copyColWidth;
  const deleteColLeft = _iconLeftAcc; _iconLeftAcc += deleteColWidth;
  const reorderColLeft = _iconLeftAcc; _iconLeftAcc += reorderColWidth;
  const editColLeft = _iconLeftAcc; _iconLeftAcc += editColWidth;
  // No vertical divider between the icon columns (2026-08-23, Alex: "remove the
  // vertical lines between the icons" — reversing the 2026-08-22 pass that added
  // them). `none`, not omitted, so these cells don't fall back to tdBase's/
  // thStyle's own borderRight either.
  const iconTdSticky = (left) => ({ position: 'sticky', left, zIndex: 1, background: 'inherit', borderRight: 'none' });
  const iconThSticky = (left) => ({ position: 'sticky', left, zIndex: 3, borderRight: 'none' });

  // UX-7 — FREEZE THE LEADING DATA COLUMNS (Alex, 2026-07-30: "we should freeze all
  // columns up to Size for catalog datagrid"). Scroll sideways to reach RM Source or
  // FabRate and Tag/Label scroll away, so you lose which ROW you are reading.
  //
  // Not a new mechanism: column 0 has ALWAYS been sticky at left:iconColsWidth, and
  // the four icon columns are a sticky block with cumulative offsets. This just says
  // how MANY data columns join that block, each pinned at the summed width of the
  // ones before it -- reading the LIVE colWidths, so a resize moves the pin with it
  // rather than leaving a gap or an overlap.
  //
  // Depends on UX-8 landing first: a frozen column whose text wraps makes the row
  // tall AND permanently visible.
  const stickyDataCols = Math.max(1, Math.min(Number(stickyCols) || 1, columns.length));
  const dataColLeft = (ci) => iconColsWidth + colWidths.slice(0, ci).reduce((s, w) => s + (w || 0), 0);
  // The LAST frozen column carries the seam -- one rule marking where the frozen
  // block ends, so a scrolled grid reads as two regions instead of an accident.
  //
  // ⚠ A STICKY CELL PAINTS ITS OWN RIGHT EDGE (Alex, 2026-08-04: "the vertical lines in
  // the frozen part of the grid are transparent and I can detect the text of the hidden
  // sliding text left right passing underneath"). With border-collapse:collapse the
  // border between two cells belongs to the TABLE's paint layer, which is below the
  // sticky cell's own background -- so the scrolled content showed through that exact
  // 1px line. An inset box-shadow is painted by the CELL, so it travels and covers with
  // it. The border itself is removed on these cells so the two cannot double up.
  const dataStickyTd = (ci) => (ci >= stickyDataCols ? {} : {
    position: 'sticky', left: dataColLeft(ci), zIndex: 1, background: 'inherit',
    borderRight: 'none',
    boxShadow: ci === stickyDataCols - 1
      ? 'inset -2px 0 0 var(--th-border)'
      : 'inset -1px 0 0 var(--th-border-light)',
  });
  const dataStickyTh = (ci) => (ci >= stickyDataCols ? {} : {
    position: 'sticky', left: dataColLeft(ci), zIndex: 3,
    borderRight: 'none',
    boxShadow: ci === stickyDataCols - 1
      ? 'inset -2px 0 0 var(--th-border)'
      : 'inset -1px 0 0 var(--th-border)',
  });

  // Sub-row bars (Save/Cancel, delete confirm, delete warning) live in ONE
  // `<td colSpan={every column}>`, so the cell is as wide as the whole table
  // while its buttons sit at the cell's left edge. On a grid wide enough to
  // scroll sideways that puts them off-screen the moment you scroll right to
  // reach a column — and a control you cannot see reads as a control that does
  // not exist (Alex, 2026-07-29: "they disappear, and I thought we didn't even
  // have it").
  //
  // Same cure the icon columns already use: pin the CONTENT, not the cell.
  // inline-flex so the sticky box is only as wide as the buttons; without it a
  // block-level child fills the cell and sticky has nothing to hold back.
  // THE ORDER IN EVERY BAR IS: BUTTONS FIRST, THEN THE WORDS. Alex, 2026-08-08,
  // on the delete confirm — "swap button w/ text (typical)". The edit bar already
  // read Save/Cancel then the reason (see ERR-1(b) below: "the message sits with
  // the control you are about to press again"); the three delete/warning bars had
  // it the other way round, so the button you reach for moved depending on which
  // bar opened. Same rule everywhere now — put new bars' controls first too.
  const subRowBar = {
    position: 'sticky', left: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
    maxWidth: '100%',
  };

  // F-uismalls (header restyle v-next): smaller muted text, 2px solid bottom
  // border instead of a heavy background fill — background lightened to
  // transparent. Sticky/resize/sort affordances and column widths unchanged.
  const thStyle = {
    position: 'sticky', top: 0, zIndex: 2,
    padding: 'var(--th-header-pad, 4px 8px)',
    textAlign: 'left', fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--th-text-secondary)',
    // FRAME ROWS SIT ON THE ROW COLOUR (Alex, 2026-08-01, refining "they are too
    // different"): "can it also be same background color... the bold font and the
    // horizontal line slightly thicker." That 2px rule was softened to the SAME thin
    // line as every ordinary row divider on 2026-08-23 (Alex: "horizontal line for
    // header and footer split same thin line as all the other") — header/body/footer
    // are now separated by font weight alone, not by a heavier rule. Supersedes the
    // grey frame tried earlier the same day; the two tokens that disagreed
    // (--th-header-bg vs --th-row-even) are settled by both using --th-row-even.
    whiteSpace: 'nowrap', background: 'var(--th-row-even)',
    // Vertical column divider matched to the body's own tdBase (--th-border-light) —
    // was --th-border, a full step darker (Alex, 2026-08-22 check: "can the header and
    // total row vertical line be exactly the same as the standard row").
    borderRight: '1px solid var(--th-border-light)',
    borderBottom: '1px solid var(--th-border-light)',
    userSelect: 'none',
  };
  const tdBase = {
    padding: 'var(--th-cell-pad, 2px 6px)',
    verticalAlign: 'middle',
    borderRight: '1px solid var(--th-border-light)',
    overflow: 'hidden',
  };

  // UX-8 — put the full text on `title`, but ONLY where the cell is actually
  // truncated. Read from the DOM rather than from the column's data because
  // several columns render JSX (a two-line Labour cell, the ƒ badge + formula,
  // a money cell's split $ and amount), so there is no single row field that
  // holds what the user is looking at — `title={row[col.key]}` would come out
  // blank or [object Object]. textContent IS the rendered text, by definition.
  //
  // Cheap: one pass over the visible cells, and only cells whose content
  // overflows get an attribute at all — no tooltip appears on a cell that is
  // already fully readable, which is the difference between a help and a
  // nuisance. A cell holding a fill control is skipped (see data-tg-truncate).
  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    for (const td of el.querySelectorAll('tbody td[data-tg-truncate]')) {
      // A COMPOSITE cell is skipped. textContent concatenates every part, so a
      // status-count roll-up tooltips as "✎ 252✓ 361↗ 17" -- text that exists
      // nowhere on screen and means nothing. Measured on the catalog folder grid,
      // which is why this guard is here. One element child (or none) is a single
      // text run: that is the case a tooltip can actually complete.
      if (td.childElementCount > 1) { td.removeAttribute('title'); continue; }
      // +1: sub-pixel widths make scrollWidth exceed clientWidth by a fraction on
      // cells that are not really clipped, which would tooltip half the grid.
      if (td.scrollWidth > td.clientWidth + 1) {
        const text = (td.textContent || '').trim();
        if (text) td.setAttribute('title', text); else td.removeAttribute('title');
      } else {
        td.removeAttribute('title');
      }
    }
  });

  // §3 — Buffer a cell value locally; does NOT call onSave.
  function bufferCell(rowId, colKey, value) {
    setRowBuffer(prev => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), [colKey]: value },
    }));
  }

  // S2.2 — the "original" value a buffered cell is compared against for change-detection:
  // editValue(row) when the column defines one (so a column whose edit control seeds/reads
  // a DIFFERENT field than col.key — e.g. a list param's Typical, seeded from typical_text —
  // is compared against THAT field, not the raw row[colKey]); row[colKey] otherwise
  // (unchanged for every column without editValue).
  function origCellValue(colKey, row) {
    const col = columns.find(c => c.key === colKey);
    return (col && col.editValue) ? col.editValue(row) : row[colKey];
  }

  // §3 — Commit all buffered values for the row by calling onSave per changed cell.
  //
  // AWAITED AND CAUGHT (2026-08-05). This used to be `onSave(...)` fire-and-forget: the
  // result was discarded, so the grid never learned whether a save worked, and every
  // consumer had to catch its own failure and decide where to show it. They did that
  // differently, or not at all — which is how a refused rename read as "it failed
  // silently" while the reason sat at the top of the page. onAdd and onDelete already
  // awaited and placed their messages inline; save was the one that didn't.
  //
  // A failing save also KEEPS THE ROW IN EDIT MODE. The value that was rejected is still
  // in the buffer, next to the reason and the Save button — you can correct it and press
  // again. Dropping out of edit mode would discard the input and leave nothing to fix.
  async function commitRow(row) {
    const buf = rowBuffer[String(row.id)] || {};
    if (onSave) {
      for (const [colKey, value] of Object.entries(buf)) {
        // Only save cells that actually changed
        if (String(value) !== String(origCellValue(colKey, row) ?? '')) {
          try {
            await onSave(row.id, colKey, value);
          } catch (err) {
            setSaveError({ rowId: String(row.id), message: err?.message || 'Could not save this row.' });
            return;   // buffer and edit mode intact, so the value can be fixed
          }
        }
      }
    }
    setSaveError(null);
    setRowBuffer(prev => { const n = { ...prev }; delete n[String(row.id)]; return n; });
    onEditRow?.(null);
  }

  // §3 — Discard all buffered values and exit edit mode.
  function cancelRow() {
    setRowBuffer({});
    onEditRow?.(null);
  }

  // editOnRowClick — is the row currently in edit mode dirty (any buffered value differs
  // from the underlying row)? Used to protect against silent data loss: while a dirty row
  // is open, a click on another row is ignored until Save/Cancel.
  function isEditRowDirty() {
    if (editRowId == null) return false;
    const buf = rowBuffer[String(editRowId)];
    if (!buf) return false;
    const editRow = (rows || []).find(r => String(r.id) === String(editRowId));
    if (!editRow) return false;
    return Object.entries(buf).some(([k, v]) => String(v) !== String(origCellValue(k, editRow) ?? ''));
  }

  // editOnRowClick — pre-populate the buffer with current values for editable columns
  // (mirrors the ✎ button) and enter edit mode for the clicked row.
  function enterEditMode(row) {
    // The other half of "one open thing" — see openAdd. An add in progress closes
    // when a row opens for edit, so the grid never shows two live editors.
    if (addFormOpen) { setAddFormOpen(false); onAddOpenChange?.(false); }
    const initBuf = {};
    for (const col of columns) {
      if (cellIsEditable(col, row)) {
        const seed = col.editValue ? col.editValue(row) : row[col.key];
        initBuf[col.key] = String(seed ?? '');
      }
    }
    setRowBuffer(prev => ({ ...prev, [String(row.id)]: initBuf }));
    onEditRow?.(row.id);
  }

  // editOnRowClick — row-click trigger. Clears any pending delete UI, then either enters
  // edit mode for the clicked row or (if another row is already open and dirty) ignores
  // the click so no buffered edit is lost. No auto-commit.
  function handleEditRowClick(row) {
    setConfirmingDelete(null);
    setDeleteWarning(null);
    if (rowLocked?.(row)) return; // INF-14 — the reason is on the row's hover; nothing to open
    const clickedIsEditing = editRowId != null && String(row.id) === String(editRowId);
    if (clickedIsEditing) return; // already editing this row — leave it open
    if (editRowId != null && isEditRowDirty()) return; // another dirty row open — ignore (no data loss)
    enterEditMode(row);
  }

  // Reorder drag: compute the final 0-based index for the dragged row when dropped
  // onto `overIndex` at `edge`. Accounts for the source being removed first (backend
  // splices remove-then-insert), so dropping just below the source is a no-op.
  function computeDropIndex(srcIndex, overIndex, edge) {
    let insertAt = edge === 'bottom' ? overIndex + 1 : overIndex;
    if (srcIndex < insertAt) insertAt -= 1; // removing source shifts later positions down
    return Math.max(0, Math.min(rows.length - 1, insertAt));
  }

  async function commitAdd() {
    if (!onAdd) return;
    try {
      await onAdd(addFields);
      setAddActive(false);
      setAddFields({});
      setAddError(null);
    } catch (err) {
      // Inline ⚠ under the add row — consistent with the delete-warning (no alert popups).
      setAddError(err?.message || 'Could not add this row.');
    }
  }

  function cancelAdd() {
    setAddActive(false);
    setAddFields({});
    setAddError(null);
  }

  function startColResize(colIdx, e) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidthsRef.current[colIdx];
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      const newW = Math.max(40, startW + ev.clientX - startX);
      const next = [...colWidthsRef.current];
      next[colIdx] = newW;
      colWidthsRef.current = next;
      setColWidths(next);
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (storageKey) localStorage.setItem(`inoviu:cols:v2:${storageKey}`, JSON.stringify(colWidthsRef.current));
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  if (loading) {
    return <Text kind="note" as="div" style={{ padding: 20 }}>Loading…</Text>;
  }

  const hasRows = rows && rows.length > 0;
  // C7: an empty grid with no onAdd still renders headers + a single empty-message
  // row (falls through to the table below) instead of a bare message line, as long
  // as columns are provided — keeps the grid's presence visible even when empty.
  if (!hasRows && !onAdd && !(columns && columns.length)) {
    return (
      <Text kind="note" as="div" style={{ padding: '20px 0', textAlign: 'center' }}>
        {emptyMessage || 'No items.'}
      </Text>
    );
  }

  return (
    <div>
      {/* Bulk action bar (multiSelect) — renders ONLY while >=1 row is selected (nothing
          at rest). Reuses onDelete/onCopy/onReorder — buttons only appear when the
          consumer actually wired that handler, mirroring the single-row icon columns. */}
      {multiSelect && (toolbar || bulkSelected.size > 0) && !bulkDeleteConfirm && !bulkResult && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          // NOT flexWrap:'wrap' when a toolbar pins the bar: wrapping is a second way
          // for this row to change height, which is the thing being fixed.
          flexWrap: toolbar ? 'nowrap' : 'wrap',
          // ⚠ A FIXED height, not a minimum. minHeight left three ways for the row to
          // grow and I found all three by measuring: taller buttons (54px), wrapped
          // button labels on a narrow window (68px), and finally the horizontal
          // scrollbar that overflowX:auto adds BELOW the content (57px). A fixed box
          // cannot grow by any of them; overflow scrolls inside it.
          height: toolbar ? 34 : undefined,
          boxSizing: toolbar ? 'border-box' : undefined,
          overflowX: toolbar ? 'auto' : undefined,
          overflowY: toolbar ? 'hidden' : undefined,
          padding: toolbar ? '0 10px' : '5px 10px', marginBottom: 6,
          // At rest with a toolbar the bar is chrome, not a selection — so it stays
          // neutral and only lights up once rows are actually picked.
          background: bulkSelected.size > 0 ? 'var(--th-row-selected)' : 'transparent',
          border: `1px solid ${bulkSelected.size > 0 ? 'var(--th-border)' : 'transparent'}`,
          borderLeft: `3px solid ${bulkSelected.size > 0 ? 'var(--th-row-selected-border)' : 'transparent'}`,
          borderRadius: 3,
          fontSize: 'var(--fs-small)',
        }}>
          {/* ACTIONS LEFT, STATE RIGHT (Alex, 2026-08-19). I first put the toolbar
              first and he corrected it against his own sketch: "the copy and paste,
              etc., are typically on the left side, versus the status is on the right
              side." So the things you DO to the rows lead, and the things that
              DESCRIBE the rows trail — the toolbar renders after, and pushes its own
              right-hand group over with marginLeft:auto. */}
          {bulkSelected.size > 0 && (
            <>
              <strong style={{ color: 'var(--th-text-secondary)', whiteSpace: 'nowrap' }}>{bulkSelected.size} selected</strong>
              {/* Same red as the confirm button below, not bulkBtnStyle's neutral grey
                  (Alex, 2026-08-21: "same red when red" — Delete is Delete whether
                  it's the trigger or the confirm one click later). Height/padding
                  still come from bulkBtnStyle so the row-jitter fix holds; only the
                  color is overridden. */}
              {onDelete && <button onClick={handleBulkDeleteClick} disabled={bulkBusy} style={{ ...bulkBtnStyle, background: 'var(--tg-delete-bg)', color: 'var(--th-row-even)', border: 'none' }}>✕ Delete</button>}
              {onReorder && <button onClick={() => handleBulkMove('up')} disabled={bulkBusy} style={bulkBtnStyle}>↑ Move up</button>}
              {onReorder && <button onClick={() => handleBulkMove('down')} disabled={bulkBusy} style={bulkBtnStyle}>↓ Move down</button>}
              {onCopy && <button onClick={handleBulkCopy} disabled={bulkBusy} style={bulkBtnStyle}>⧉ Copy</button>}
              <button onClick={clearBulkSelection} disabled={bulkBusy} style={bulkBtnStyle}>Clear</button>
            </>
          )}
          {toolbar}
        </div>
      )}
      {/* Bulk delete — warning fires BEFORE confirm (Contract STANDARD), same as the
          single-row ✕: onDeleteCheck already ran for every selected row by the time
          this renders; blocked rows are listed, clear rows await this explicit confirm. */}
      {multiSelect && bulkDeleteConfirm && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          padding: '5px 10px', marginBottom: 6,
          background: 'var(--tg-warn-bg)', borderLeft: 'var(--tg-warn-border-left)',
          fontSize: 'var(--fs-small)', color: 'var(--tg-warn-color)',
        }}>
          <button
            onClick={confirmBulkDelete}
            disabled={bulkDeleteConfirm.clear.length === 0}
            style={BTN_DESTRUCTIVE}
          >Delete</button>
          <button onClick={cancelBulkDelete} style={bulkBtnStyle}>Cancel</button>
          <span>
            ⚠ Delete {bulkDeleteConfirm.clear.length} row(s)?
            {bulkDeleteConfirm.blocked.length > 0 && ` ${bulkDeleteConfirm.blocked.length} blocked — ${bulkDeleteConfirm.blocked.slice(0, 3).map(b => b.message).join('; ')}${bulkDeleteConfirm.blocked.length > 3 ? `, +${bulkDeleteConfirm.blocked.length - 3} more` : ''}`}
          </span>
        </div>
      )}
      {/* Bulk delete result — dismissible, mirrors the per-row inline warning styling. */}
      {multiSelect && bulkResult && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          padding: '5px 10px', marginBottom: 6,
          background: bulkResult.skipped.length ? 'var(--tg-warn-bg)' : 'var(--th-row-even)',
          borderLeft: bulkResult.skipped.length ? 'var(--tg-warn-border-left)' : '3px solid var(--th-border)',
          fontSize: 'var(--fs-small)', color: bulkResult.skipped.length ? 'var(--tg-warn-color)' : 'var(--th-text-secondary)',
        }}>
          <span>
            {bulkResult.skipped.length > 0 ? '⚠ ' : ''}{bulkResult.deletedCount} deleted, {bulkResult.skipped.length} skipped
            {bulkResult.skipped.length > 0 && ` — ${bulkResult.skipped.slice(0, 3).map(s => s.message).join('; ')}${bulkResult.skipped.length > 3 ? `, +${bulkResult.skipped.length - 3} more` : ''}`}
          </span>
          <button onClick={() => setBulkResult(null)} style={bulkBtnStyle}>OK</button>
        </div>
      )}
      {/* THE table's own horizontal scroll (2026-08-20, Alex: "should the scroll bar
          belong to the DataGrid instead? What is typical?" — yes: a table wider than
          its container scrolls itself, with whatever sits above it (toolbar, a
          card's title) staying put. Wrapping only the table — not the bulk-action
          bar above — keeps that bar fixed while the rows scroll under it, and gives
          the sticky icon/first-data columns the CORRECT nearest-scrolling-ancestor to
          stick relative to. Previously this lived one level up, on the card itself
          (CARD_STYLE) — that dragged the card's own TITLE out of view on scroll,
          since the title sat inside the same scrolling box as the grid. */}
      <div style={{ overflowX: 'auto' }}>
      <table ref={tableRef} style={{
        tableLayout: 'fixed', width: totalWidth,
        borderCollapse: 'collapse',
        // Left/right only — NOT top/bottom. The header's own borderBottom and a
        // footer's own borderTop (both 2px var(--th-border)) already close those
        // edges; a matching 1px table-level top/bottom doubled them right next to
        // a Card's own border a few px further out, reading as a raised inset
        // panel rather than flush with the card (Alex, 2026-08-22: "that grid
        // looks like it is in 3D on top of the card... maybe removing the
        // horizontal line on top of the heading or on the bottom of the total").
        // No footer: the last row's own 1px border-light closes it instead —
        // lighter, matching the ordinary row dividers rather than a second frame.
        borderLeft: '1px solid var(--th-border)',
        borderRight: '1px solid var(--th-border)',
        fontSize: 'var(--th-font-size)',
      }}>
        <colgroup>
          {onCopy && <col style={{ width: copyColWidth }} />}
          {onDelete && <col style={{ width: deleteColWidth }} />}
          {onReorder && <col style={{ width: reorderColWidth }} />}
          {showEditCol && <col style={{ width: editColWidth }} />}
          {columns.map((c, ci) => <col key={c.key} style={{ width: colWidths[ci] }} />)}
        </colgroup>
        <thead>
          <tr style={{ background: 'var(--th-row-even)', borderBottom: '2px solid var(--th-border)' }}>
            {onCopy && <th style={{ ...thStyle, ...iconThSticky(copyColLeft) }} />}
            {onDelete && <th style={{ ...thStyle, ...iconThSticky(deleteColLeft) }} />}
            {onReorder && <th style={{ ...thStyle, ...iconThSticky(reorderColLeft) }} />}
            {showEditCol && <th style={{ ...thStyle, ...iconThSticky(editColLeft) }} />}
            {columns.map((c, ci) => (
              <th key={c.key} style={{ ...thStyle, textAlign: c.align || 'left', ...dataStickyTh(ci) }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', paddingRight: (storageKey && c.resizable !== false) ? 6 : 0 }}>{c.label}</span>
                {storageKey && c.resizable !== false && (
                  <div
                    onMouseDown={e => startColResize(ci, e)}
                    style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'col-resize', zIndex: 1 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--th-accent)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!hasRows && (
            <tr>
              <td
                colSpan={columns.length + extraCols}
                style={{ ...tdBase, borderLeft: 'var(--tg-node-inactive-border)', color: 'var(--tg-code-color-other)', fontSize: 'var(--fs-small)', textAlign: 'center', padding: 16 }}
              >
                {emptyMessage || 'No items.'}
              </td>
            </tr>
          )}
          {(rows || []).map((row, i) => {
            // Delete warning / confirm render as a sub-row UNDER the (still visible) row —
            // same placement as the edit Save/Cancel bar — so it's unambiguous which row
            // they refer to (replacing the row made the message look like it belonged to
            // the row above).
            const showDeleteWarning = onDelete && deleteWarning && String(row.id) === deleteWarning.rowId;
            const showDeleteConfirm = onDelete && !showDeleteWarning && String(row.id) === confirmingDelete;
            const isSelected = selectedRowId != null && String(row.id) === String(selectedRowId);
            const isBulkSelected = multiSelect && bulkSelected.has(String(row.id));
            // A row being edited via the onEditRow/editRowId lane matches its OWN
            // Save/Cancel bar below (isRowInEditMode further down hardcodes the same
            // --th-row-selected). Computed here, not just there, so the row and its bar
            // are always ONE seamless block regardless of whether the caller separately
            // wired selectedRowId to track the same id — most don't (HeaderRow never
            // takes a selectedRowId prop at all), which is why the header rows (Product,
            // Category, Configuration params) sat white under a blue bar (Alex, 2026-08-22:
            // "the ones that belong to the main header... it's not blue; it's still white").
            const isRowInEditMode = onEditRow != null && editRowId != null && String(row.id) === String(editRowId);
            // NO ZEBRA BANDING (Alex, 2026-08-01: "No banded row with different grey,
            // all same. I would like to test that look"). One flat surface — the column
            // rules and the per-row divider already give the eye its horizontal track,
            // and the banding was competing with the selected/hover tints for the same
            // signal. --th-row-odd stays in theme.css unused, so this is one line to undo.
            const rowBg = (isSelected || isBulkSelected || isRowInEditMode)
              ? 'var(--th-row-selected)'
              : 'var(--th-row-even)';
            // Multi-select "boxed cell" fix: with several rows selected, the per-row
            // borderBottom divider (always present, every row) cut across the shared
            // selected background between CONTIGUOUS bulk-selected rows — combined with
            // the per-column borderRight, that tiled the highlight into a grid of little
            // boxes instead of one clean block. selectedRowId never showed this because
            // only one row is ever selected, so there's no adjacent same-colored row for
            // a divider to cut across. Dropping the divider only between two adjacent
            // bulk-selected rows merges them into a single seamless band, matching the
            // selectedRowId look, while the block's outer top/bottom edges (against
            // unselected neighbors) keep their border as usual.
            const nextRow = (rows || [])[i + 1];
            const nextAlsoBulkSelected = isBulkSelected && !!nextRow && bulkSelected.has(String(nextRow.id));
            // Reorder (contract v15) — 2px drop-indicator line at the edge nearest the cursor.
            const isDropTarget = onReorder && dropTarget && dropTarget.index === i;
            const dropShadow = isDropTarget
              ? (dropTarget.edge === 'top'
                  ? 'inset 0 2px 0 0 var(--th-accent)'
                  : 'inset 0 -2px 0 0 var(--th-accent)')
              : undefined;
            // A row that is SAYING something about itself (Onshape-owned, failed to
            // resolve) keeps saying it while hovered. Alex, 2026-08-04: "as soon as I
            // hover on, the grey disappear. I checked on the catalog filter and it does
            // not disappear. It is not done based on the same concept!" -- exactly right:
            // the catalog's status filter dims with OPACITY, which hover never touches,
            // while these set a background, which the hover handler overwrote inline and
            // then restored to rowBg, losing the state colour for good.
            const rowStateStyle = getRowStyle ? (getRowStyle(row) || {}) : {};
            const restBg = rowStateStyle.background || rowBg;
            const trStyle = {
              background: rowBg,
              borderBottom: nextAlsoBulkSelected ? 'none' : '1px solid var(--th-border-light)',
              cursor: (onRowClick || onRowDoubleClick || (editOnRowClick && onEditRow)) ? 'pointer' : 'default',
              // multiSelect: kill native text-selection so Shift-click range-selects ROWS
              // instead of dragging a browser text selection across the rows (which visually
              // overrode the row highlight — "Shift didn't work" while Ctrl-toggle did).
              ...(multiSelect ? { userSelect: 'none', WebkitUserSelect: 'none' } : {}),
              ...(dropShadow ? { boxShadow: dropShadow } : {}),
              ...rowStateStyle,
            };
            const lockReason = rowLocked?.(row) || null;
            // The ✕ slot says whose row it is instead of offering a delete the server refuses.
            const deleteBlockedNode = deleteBlockedIcon?.(row)
              ?? (lockReason ? <span title={lockReason}>{LOCKED}</span> : null);
            // renderSubRow gets offsetOf too (2026-08-04). renderExpandedRow has had it
            // since UX-17.1 so the Function screen's config block can line up UNDER a
            // column and stay lined up through a resize; the component editor needs the
            // same for the same reason (Alex: "adjust the layout to have all the input
            // data almost in line"). A hardcoded indent drifts the first time a column
            // width changes.
            const subContent = renderSubRow && isSelected ? renderSubRow(row, { offsetOf: expandedOffsetOf }) : null;
            const expandedContent = renderExpandedRow && expandedRowIds?.has(String(row.id))
              ? renderExpandedRow(row, { offsetOf: expandedOffsetOf }) : null;

            // §3 — get buffer for this row
            const buf = rowBuffer[String(row.id)] || {};

            // editColSpan (2026-08-21) — an editRender column may declare
            // `editColSpan: N` to claim the N-1 columns immediately after it too,
            // for THIS row only, while it's the inlineEditRowId (e.g. ComponentsGrid's
            // Ref cell reclaiming the Label cell's width while picking a new
            // component — Label has nothing useful to show mid-pick). Computed once
            // per row: which column KEYS the loop below must skip because an earlier
            // column already spanned over them. Columns that never set editColSpan
            // behave byte-identically — this set stays empty.
            const isThisRowInlineEdit = inlineEditRowId != null && String(row.id) === String(inlineEditRowId);
            const editColSpanSkip = new Set();
            if (isThisRowInlineEdit) {
              columns.forEach((c, i) => {
                if (typeof c.editRender === 'function' && c.editColSpan > 1) {
                  for (let k = 1; k < c.editColSpan && i + k < columns.length; k++) editColSpanSkip.add(columns[i + k].key);
                }
              });
            }

            return (
              <Fragment key={row.id}>
              <tr
                style={trStyle}
                // Lets a screen bring one row into view (Catalog: a family opened from
                // Onshape's "Open in DB"). An attribute, not a ref API — read-only.
                data-row-id={String(row.id)}
                title={lockReason || undefined}
                onClick={(e) => {
                  // editOnRowClick: click enters edit mode (interactive children stopPropagation
                  // so they never reach here). Otherwise: default SELECT behavior (unchanged).
                  // ⚠ MUST come AFTER the multiSelect modifier check below, not before — a grid
                  // that has BOTH editOnRowClick and multiSelect (2026-08-23, CatalogView's L3
                  // family/line grid: "one click to edit everywhere" landed on a grid that
                  // already had bulk actions) needs Shift/Ctrl-click to keep doing bulk-select;
                  // firing the edit shortcut unconditionally here made the whole "N selected"
                  // bar unreachable on that grid, since every modifier-click just opened edit.
                  const isMultiSelectGesture = multiSelect && (e.shiftKey || e.ctrlKey || e.metaKey);
                  if (!isMultiSelectGesture) onRowSelect?.(row);
                  if (!isMultiSelectGesture && editOnRowClick && onEditRow) { handleEditRowClick(row); return; }
                  if (multiSelect) {
                    const idStr = String(row.id);
                    if (e.shiftKey && bulkAnchor != null) {
                      const ids = (rows || []).map(r => String(r.id));
                      const anchorIdx = ids.indexOf(String(bulkAnchor));
                      if (anchorIdx !== -1) {
                        const from = Math.min(anchorIdx, i), to = Math.max(anchorIdx, i);
                        setBulkSelected(new Set(ids.slice(from, to + 1)));
                        // Shift/Ctrl are new bulk-only gestures (no prior defined meaning) — they
                        // don't call onRowClick, but still clear any stale per-row confirm/warning
                        // sub-row exactly like a normal click always has.
                        setConfirmingDelete(null); setDeleteWarning(null);
                        return;
                      }
                    }
                    if (e.ctrlKey || e.metaKey) {
                      setBulkSelected(prev => {
                        const next = new Set(prev);
                        if (next.has(idStr)) next.delete(idStr); else next.add(idStr);
                        return next;
                      });
                      setBulkAnchor(row.id);
                      setConfirmingDelete(null); setDeleteWarning(null);
                      return;
                    }
                    // Plain click (or shift-click with no anchor yet): select just this row
                    // and make it the new range anchor. Falls through to onRowClick below —
                    // plain-click SELECT behavior is unchanged (Contract: these grids do NOT
                    // edit-on-row-click, so this gesture is free to double as bulk-select).
                    setBulkSelected(new Set([idStr]));
                    setBulkAnchor(row.id);
                  }
                  setConfirmingDelete(null); setDeleteWarning(null);
                  // "One open thing", third path. Some grids (COMPONENTS) do not use
                  // DataGrid's editRowId at all — they open their own inline editor via
                  // onRowClick + inlineEditRowId, which is invisible to the openAdd /
                  // enterEditMode guards. That is exactly the pair Alex hit: a row
                  // editor and the add form on screen together. Closing the add lane
                  // here covers those grids too; the reverse direction is the
                  // consumer's, via onAddOpenChange.
                  if (addFormOpen) { setAddFormOpen(false); onAddOpenChange?.(false); }
                  onRowClick?.(row);
                }}
                onDoubleClick={e => {
                  // §4: do NOT drill when row is in edit mode or target is an editable input
                  if (isRowInEditMode) return;
                  const tag = e.target.tagName;
                  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
                  onRowDoubleClick?.(row);
                }}
                onMouseEnter={e => { if (!isSelected && !rowStateStyle.background) e.currentTarget.style.background = 'var(--th-row-hover)'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = restBg; }}
                {...(onReorder ? {
                  onDragOver: e => {
                    if (!dragSrcRef.current) return;
                    e.preventDefault(); // allow drop
                    const rect = e.currentTarget.getBoundingClientRect();
                    const edge = (e.clientY - rect.top) < rect.height / 2 ? 'top' : 'bottom';
                    setDropTarget(prev => (prev && prev.index === i && prev.edge === edge) ? prev : { index: i, edge });
                  },
                  onDrop: e => {
                    e.preventDefault();
                    const src = dragSrcRef.current;
                    if (src) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const edge = (e.clientY - rect.top) < rect.height / 2 ? 'top' : 'bottom';
                      const target = computeDropIndex(src.index, i, edge);
                      if (target !== src.index) onReorder(src.rowId, target);
                    }
                    dragSrcRef.current = null;
                    setDropTarget(null);
                  },
                  onDragEnd: () => { dragSrcRef.current = null; setDropTarget(null); },
                } : {})}
              >
                {/* A4 (overnight batch): icon columns (copy/delete/reorder/edit) now render
                    FIRST — sticky-left block at left:0..iconColsWidth, always in the same
                    place regardless of grid width/scroll. */}
                {onCopy && (
                  <td style={{ ...tdBase, textAlign: 'center', padding: '0 2px', ...iconTdSticky(copyColLeft) }}>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setConfirmingDelete(null);
                        setDeleteWarning(null);
                        // Awaited and caught, like save and delete. ERR-1(b) names three
                        // verbs — "save/delete/copy failures appear next to Save/Cancel" —
                        // and copy was the last one still failing into the void. A copy CAN
                        // fail for a real reason (no free tag left under this shelf), and
                        // that sentence belongs on the row you pressed it on.
                        Promise.resolve(onCopy(row)).catch(err => {
                          setSaveError({ rowId: String(row.id), message: err?.message || 'Could not copy this row.' });
                        });
                      }}
                      title="Copy"
                      style={{ ...BTN_GLYPH, lineHeight: 1 }}
                    >⧉</button>
                  </td>
                )}
                {onDelete && deleteBlockedNode != null && (
                  <td style={{ ...tdBase, textAlign: 'center', padding: '0 4px', ...iconTdSticky(deleteColLeft) }}>
                    <span style={{ color: 'var(--tg-icon-color)', lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}
                          onClick={e => e.stopPropagation()}>
                      {deleteBlockedNode}
                    </span>
                  </td>
                )}
                {onDelete && deleteBlockedNode == null && (
                  <td style={{ ...tdBase, textAlign: 'center', padding: '0 4px', ...iconTdSticky(deleteColLeft) }}>
                    <button
                      onClick={async e => {
                        e.stopPropagation();
                        // Last-action-wins (Alex 2026-07-12): opening the delete guard
                        // closes any open row-edit bar so the two never stack.
                        onEditRow?.(null);
                        // STANDARD (Alex 2026-07-02): the delete-guard runs on the ✕ click,
                        // BEFORE any confirm — never after. onDeleteCheck may return the
                        // warning string synchronously OR a Promise of one (usage checks
                        // that need a server round-trip).
                        if (onDeleteCheck) {
                          let warning = onDeleteCheck(row);
                          if (warning && typeof warning.then === 'function') {
                            try { warning = await warning; } catch { warning = null; }
                          }
                          if (warning) { setDeleteWarning({ rowId: String(row.id), message: warning }); setConfirmingDelete(null); return; }
                        }
                        setDeleteWarning(null);
                        setConfirmingDelete(String(row.id));
                      }}
                      title="Delete row"
                      style={{ ...BTN_GLYPH, lineHeight: 1 }}
                    >✕</button>
                  </td>
                )}
                {/* Reorder column (contract v16) — drag grip only. */}
                {onReorder && (
                  <td style={{ ...tdBase, textAlign: 'center', padding: '0 2px', ...iconTdSticky(reorderColLeft) }}>
                    <span
                      draggable
                      onDragStart={e => {
                        e.stopPropagation();
                        dragSrcRef.current = { rowId: row.id, index: i };
                        e.dataTransfer.effectAllowed = 'move';
                        // Firefox requires data to be set for the drag to start.
                        try { e.dataTransfer.setData('text/plain', String(row.id)); } catch {}
                      }}
                      onClick={e => e.stopPropagation()}
                      title="Drag to reorder"
                      style={{ ...BTN_GLYPH, cursor: 'grab', lineHeight: 1, userSelect: 'none', padding: '0 1px' }}
                    >⠿</span>
                  </td>
                )}
                {/* §3: edit column — ✎ icon only (Save/Cancel render as a sub-row under the edited row).
                    Hidden when editOnRowClick is on (row-click is the trigger; no ✎ icon). */}
                {showEditCol && (() => {
                  if (isRowInEditMode) {
                    // In edit mode: show a muted indicator; Save/Cancel render in the sub-row below this row
                    return (
                      <td style={{ ...tdBase, textAlign: 'center', padding: '0 2px', ...iconTdSticky(editColLeft) }}>
                        <span style={{ fontSize: 'var(--fs-small)', color: 'var(--tg-save-bg)', fontWeight: 700, lineHeight: 1 }}>✎</span>
                      </td>
                    );
                  }
                  return (
                    <td style={{ ...tdBase, textAlign: 'center', padding: '0 2px', ...iconTdSticky(editColLeft) }}>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          // Last-action-wins (Alex 2026-07-12): entering edit closes any
                          // open delete-confirm/warning so the two never stack.
                          setConfirmingDelete(null); setDeleteWarning(null);
                          // Same "one open thing" rule as enterEditMode / openAdd. This
                          // path duplicates the buffer seeding rather than calling
                          // enterEditMode, so it has to close the add row itself or the
                          // two editors can still end up on screen together.
                          if (addFormOpen) { setAddFormOpen(false); onAddOpenChange?.(false); }
                          // Initialize buffer with current row values for editable columns
                          const initBuf = {};
                          for (const col of columns) {
                            if (cellIsEditable(col, row)) {
                              const seed = col.editValue ? col.editValue(row) : row[col.key];
                              initBuf[col.key] = String(seed ?? '');
                            }
                          }
                          setRowBuffer(prev => ({ ...prev, [String(row.id)]: initBuf }));
                          onEditRow(row.id);
                        }}
                        title="Edit this row"
                        style={{ ...BTN_GLYPH, lineHeight: 1, fontWeight: 400 }}
                      >✎</button>
                    </td>
                  );
                })()}
                {columns.map((col, ci) => {
                  // editColSpan — this column's <td> was already rendered as part of an
                  // earlier column's colSpan for this row; skip it entirely.
                  if (editColSpanSkip.has(col.key)) return null;

                  // §3 — get the current display value: buffer first, then row data
                  // (S2.2: falls back through editValue(row) when the column defines one,
                  // so a not-yet-buffered edit cell seeds from the same field its options
                  // are keyed by, same as enterEditMode's initBuf above)
                  const cellValue = col.key in buf ? buf[col.key] : ((col.editValue ? col.editValue(row) : row[col.key]) ?? '');
                  const tdStyle = {
                    ...tdBase,
                    // UX-8 — ONE LINE PER CELL (Alex decided 2026-07-30: "truncate with
                    // a tooltip"). Row height is set by the tallest cell, and RM Source
                    // holds the grid's longest strings, so one column was making rows
                    // ~170px tall; widening it did not fix every row. nowrap + ellipsis
                    // makes row height constant, which is ALSO what makes UX-7's frozen
                    // columns safe — a frozen column that wraps is a tall row you can
                    // never scroll away from. The full text goes on `title`, set from the
                    // cell's RENDERED text by the effect above (several columns render
                    // JSX, so title={row[col.key]} would be blank or [object Object]).
                    whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                    textAlign: col.align || 'left',
                    // The column's text kind, for every edit control in this cell, so
                    // Edit mode reads like the read text (cellStyles.js cellFont).
                    ...cellFont(col.textKind),
                    // The selected-row left marker belongs to the FIRST column only; the
                    // sticky treatment now extends across the frozen block (UX-7).
                    ...(ci === 0 ? {
                      borderLeft: (isSelected || isBulkSelected) ? '3px solid var(--th-row-selected-border)' : 'var(--tg-node-inactive-border)',
                    } : {}),
                    ...dataStickyTd(ci),
                  };

                  // S3d — in-cell custom editor: when THIS row is the inlineEditRowId and the
                  // column supplies its own editRender, render that instead of the read value.
                  // overflow:visible so a portal-less popover (e.g. the Component search-results
                  // list, absolutely positioned) escapes the cell's default overflow:hidden.
                  // Independent of the buffered edit path below (which stays dormant here — no
                  // onEditRow/onSave is wired on this grid).
                  const isInlineEdit = inlineEditRowId != null
                    && String(row.id) === String(inlineEditRowId)
                    && typeof col.editRender === 'function';
                  if (isInlineEdit) {
                    return (
                      <td key={col.key} colSpan={col.editColSpan > 1 ? col.editColSpan : undefined} style={{ ...tdStyle, padding: 2, overflow: 'visible' }}>
                        {col.editRender(row)}
                      </td>
                    );
                  }

                  // §3 — in edit mode, render editable input for editable/editType columns.
                  // Inputs route through the shared primitives (Component_Contract):
                  //   editType 'select'→SelectCell, 'checkbox'→CheckboxCell, 'formula'→FormulaCell,
                  //   'datalist' keeps the native datalist input, else (editable / 'text')→TextCell.
                  const canCellEdit = cellIsEditable(col, row) && onSave && isRowInEditMode;
                  // Row-aware editType (additive): editType may be a string (unchanged
                  // behavior) or a function (row) => string, resolved per row here.
                  const resolvedEditType = typeof col.editType === 'function' ? col.editType(row) : col.editType;
                  // Enter=commit / Escape=cancel for the free-text inputs (behavior-preserving).
                  const editKeyDown = e => {
                    if (e.key === 'Enter') { e.preventDefault(); commitRow(row); }
                    if (e.key === 'Escape') { cancelRow(); }
                  };

                  if (canCellEdit && resolvedEditType === 'select') {
                    // S1b: options may be row-aware (a function), same pattern as editType.
                    const resolvedOptions = typeof col.options === 'function' ? col.options(row) : (col.options || []);
                    return (
                      <td key={col.key} style={{ ...tdStyle, padding: 0 }}>
                        <SelectCell
                          value={cellValue}
                          options={resolvedOptions}
                          onChange={val => bufferCell(String(row.id), col.key, val)}
                        />
                      </td>
                    );
                  }

                  if (canCellEdit && resolvedEditType === 'checkbox') {
                    // A checkbox is content, not a fill control — keeps the cell padding.
                    // Its Yes/No word takes the cell's edit font, like any control here.
                    return (
                      <td key={col.key} style={{ ...tdStyle, fontSize: 'var(--tg-cell-fs)' }}>
                        <CheckboxCell
                          value={cellValue}
                          onChange={b => bufferCell(String(row.id), col.key, b ? '1' : '0')}
                        />
                      </td>
                    );
                  }

                  if (canCellEdit && resolvedEditType === 'formula') {
                    return (
                      <td key={col.key} style={{ ...tdStyle, padding: 0 }}>
                        <FormulaCell
                          value={cellValue}
                          onChange={val => bufferCell(String(row.id), col.key, val)}
                          onKeyDown={editKeyDown}
                        />
                      </td>
                    );
                  }

                  if (canCellEdit && resolvedEditType === 'datalist') {
                    const listId = `dl-${col.key}-${row.id}`;
                    return (
                      <td key={col.key} style={{ ...tdStyle, padding: 0 }}>
                        <datalist id={listId}>
                          {(col.options || []).map(opt => <option key={opt} value={opt} />)}
                        </datalist>
                        <input
                          list={listId}
                          value={String(cellValue)}
                          onChange={e => bufferCell(String(row.id), col.key, e.target.value)}
                          onClick={e => e.stopPropagation()}
                          style={INPUT_STYLE}
                        />
                      </td>
                    );
                  }

                  if (canCellEdit && (resolvedEditType === 'text' || col.editable)) {
                    return (
                      <td key={col.key} style={{ ...tdStyle, padding: 0 }}>
                        <TextCell
                          value={cellValue}
                          align={col.align}
                          onChange={val => bufferCell(String(row.id), col.key, val)}
                          onKeyDown={editKeyDown}
                        />
                      </td>
                    );
                  }

                  // Read mode — use render fn or raw value (show buffered value if present)
                  const content = col.render
                    ? col.render(
                        isRowInEditMode && col.key in buf ? { ...row, [col.key]: buf[col.key] } : row,
                        // Edit context (additive — every existing render ignores it).
                        // A column that renders its OWN editor (a cascade, a picker)
                        // used to have no way into the buffered Save/Cancel model, so
                        // it wrote straight through and the row auto-saved on click.
                        // `buffer` lets such a column stage a value like any ordinary
                        // cell — including a field OTHER than its own key, which a
                        // cascade needs (one pick, two fields). Nothing commits until
                        // Save; Cancel discards.
                        { inEdit: isRowInEditMode, buffer: bufferCell, buffered: buf },
                      )
                    : cellValue;
                  return (
                    <td key={col.key}
                        // UX-8 — marks the cells the truncation-title effect owns. A cell
                        // holding a fill control (an input/select spanning the cell) is
                        // deliberately EXCLUDED: an editor must keep its full width, and
                        // a tooltip over a control you are typing into is noise.
                        data-tg-truncate={fillsCell(content) ? undefined : '1'}
                        style={fillsCell(content) ? { ...tdStyle, padding: 0 } : tdStyle}>
                      {content}
                    </td>
                  );
                })}
              </tr>
              {subContent && (
                <tr style={{ background: 'var(--th-row-selected)' }}>
                  <td colSpan={columns.length + extraCols} style={{ padding: 0, borderLeft: '3px solid var(--th-row-selected-border)' }}>
                    {subContent}
                  </td>
                </tr>
              )}
              {expandedContent && (
                <tr>
                  <td colSpan={columns.length + extraCols} style={{ padding: 0, borderBottom: '1px solid var(--th-border-light)' }}>
                    {expandedContent}
                  </td>
                </tr>
              )}
              {/* §3 (UX Consistency 2): Save/Cancel bar as a sub-row directly UNDER the edited row */}
              {isRowInEditMode && (
                <tr style={{ background: 'var(--th-row-selected)' }}>
                  <td colSpan={columns.length + extraCols} style={{ padding: 0, borderLeft: '3px solid var(--th-row-selected-border)', borderBottom: '1px solid var(--th-border-light)' }}>
                    <div style={{ ...subRowBar, padding: '5px 8px' }}>
                      <button
                        onClick={() => commitRow(row)}
                        style={BTN_PRIMARY}
                      >Save</button>
                      <button
                        onClick={cancelRow}
                        style={BTN_SECONDARY}
                      >Cancel</button>
                      {(() => {
                        const warns = editWarnings
                          ? editWarnings(row, { ...row, ...(rowBuffer[String(row.id)] || {}) }) || []
                          : [];
                        if (!warns.length) {
                          return (
                            <Text kind="note">
                              Editing row — Save to commit, Cancel to revert.
                            </Text>
                          );
                        }
                        // Icon + words, never colour alone — Alex is colour-blind.
                        return warns.map((w, i) => (
                          <Text key={i} kind="deprecated">
                            {WARN} {w}
                          </Text>
                        ));
                      })()}
                    </div>
                  </td>
                </tr>
              )}
              {/* ERR-1(b) — the row's own failure, under the row. Placed AFTER the edit bar so
                  that while a row is being edited it reads Save/Cancel then the reason the last
                  Save was refused, in that order: the message sits with the control you are
                  about to press again. */}
              {saveError && saveError.rowId === String(row.id) && (
                <tr>
                  <td
                    colSpan={columns.length + extraCols}
                    style={{
                      padding: '5px 8px',
                      borderLeft: '3px solid var(--th-status-deprecated)',
                      borderBottom: '1px solid var(--th-border-light)',
                      background: 'var(--th-highlight-bg)',
                    }}
                  >
                    <Notice inline onDismiss={() => setSaveError(null)}>{saveError.message}</Notice>
                  </td>
                </tr>
              )}
              {/* Non-deletable warning (R5) — sub-row under the row, message + OK only. */}
              {showDeleteWarning && (
                <tr style={{ background: 'var(--tg-warn-bg)' }}>
                  <td
                    colSpan={columns.length + extraCols}
                    style={{ ...tdBase, borderLeft: 'var(--tg-warn-border-left)', borderBottom: '1px solid var(--th-border-light)', padding: '4px 10px' }}
                  >
                    <div style={subRowBar}>
                      <button
                        onClick={() => setDeleteWarning(null)}
                        style={BTN_SECONDARY}
                      >OK</button>
                      <Text kind="warn">
                        ⚠ {deleteWarning.message}
                      </Text>
                    </div>
                  </td>
                </tr>
              )}
              {/* Normal empty-node delete confirm — sub-row under the row. */}
              {showDeleteConfirm && (
                <tr style={{ background: 'var(--tg-warn-bg)' }}>
                  <td
                    colSpan={columns.length + extraCols}
                    style={{ ...tdBase, borderLeft: 'var(--tg-warn-border-left)', borderBottom: '1px solid var(--th-border-light)', padding: '4px 10px' }}
                  >
                    <div style={subRowBar}>
                    <button
                      onClick={async () => {
                        setConfirmingDelete(null);
                        // §2 (inline delete-warning): if onDelete rejects with a guard message
                        // (e.g. "in use"), surface it INLINE under the row instead of an
                        // alert()/confirm popup. Unused rows delete normally.
                        try {
                          await onDelete(row);
                        } catch (err) {
                          const msg = (err && err.message) ? err.message : 'Could not delete this row.';
                          setDeleteWarning({ rowId: String(row.id), message: msg });
                        }
                      }}
                      style={BTN_DESTRUCTIVE}
                    >Delete</button>
                    <button
                      onClick={() => setConfirmingDelete(null)}
                      style={BTN_SECONDARY}
                    >Cancel</button>
                    <Text kind="warn">
                      Delete <strong>{row.label || row.code || row.name || `row ${row.id}`}</strong>?
                      {deleteImpact?.(row) ? <> {deleteImpact(row)}</> : null}
                    </Text>
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}

          {/* Add ghost row (library lane, only when onAdd is NOT provided) — "+ {addFormLabel}"
              ghost row; clicking opens the consumer's own add form in its place. S3e: when
              addInlineEditRender/renderAddSubRow are provided, it opens IN-CELL (a blank/ghost
              row whose columns render the consumer's per-column editors + a renderAddSubRow
              extension) — the same look as inline EDIT. Otherwise it opens the full-width
              renderAddForm sub-row (unchanged). DataGrid owns only the open/closed toggle;
              onAddOpenChange lets the consumer reset a persistent add editor per open. */}
          {(() => {
            const hasAddLane = !onAdd && (renderAddForm || renderAddSubRow || addInlineEditRender);
            if (!hasAddLane) return null;
            // ONE OPEN THING. Opening the add row closes a row that is being edited,
            // exactly as clicking another row does — and it borrows that path's dirty
            // guard, so a row with unsaved changes is never silently discarded: the
            // click is ignored until the user Saves or Cancels, the same courtesy
            // row-to-row already had.
            const openAdd = () => {
              if (editRowId != null) {
                if (isEditRowDirty()) return;
                cancelRow();
              }
              setAddFormOpen(true); onAddOpenChange?.(true);
            };
            const closeAdd = () => { setAddFormOpen(false); onAddOpenChange?.(false); };
            const inCell = !!(addInlineEditRender || renderAddSubRow);
            if (!addFormOpen) {
              return (
                <tr onClick={openAdd} style={{ cursor: 'pointer' }}>
                  <td
                    colSpan={columns.length + extraCols}
                    style={{
                      ...tdBase,
                      // An empty ROW, not a thin gap: same height a body row settles
                      // at, and --th-text-secondary instead of --th-border, which at
                      // #d0d4d9 was very nearly invisible.
                      height: 'var(--th-row-min-h)',
                      borderLeft: 'var(--tg-node-inactive-border)',
                      color: 'var(--th-text-secondary)', fontSize: 'var(--fs-small)',
                    }}
                  >
                    + {addFormLabel || 'Add row'}
                  </td>
                </tr>
              );
            }
            if (!inCell) {
              return (
                <tr style={{ background: 'var(--th-row-selected)' }}>
                  <td colSpan={columns.length + extraCols} style={{ padding: 0, borderLeft: '3px solid var(--th-row-selected-border)' }}>
                    {renderAddForm(closeAdd)}
                  </td>
                </tr>
              );
            }
            // S3e — in-cell add: a blank row whose columns render addInlineEditRender editors
            // (same first-col sticky / icon-block spacing as the data + inline-edit rows), then
            // the renderAddSubRow extension below it.
            // editColSpan — same opt-in as the inline-edit row above: a column with
            // addInlineEditRender AND editColSpan:N claims the next N-1 columns' <td>s
            // for this ghost row too.
            const addEditColSpanSkip = new Set();
            columns.forEach((c, i) => {
              if (addInlineEditRender && addInlineEditRender[c.key] && c.editColSpan > 1) {
                for (let k = 1; k < c.editColSpan && i + k < columns.length; k++) addEditColSpanSkip.add(columns[i + k].key);
              }
            });
            return (
              <>
                <tr style={{ background: 'var(--th-row-selected)' }}>
                  {onCopy && <td style={{ ...tdBase, ...iconTdSticky(copyColLeft) }} />}
                  {onDelete && <td style={{ ...tdBase, ...iconTdSticky(deleteColLeft) }} />}
                  {onReorder && <td style={{ ...tdBase, ...iconTdSticky(reorderColLeft) }} />}
                  {showEditCol && <td style={{ ...tdBase, ...iconTdSticky(editColLeft) }} />}
                  {columns.map((col, ci) => {
                    if (addEditColSpanSkip.has(col.key)) return null;
                    const cellRender = addInlineEditRender && addInlineEditRender[col.key];
                    const tdStyle = {
                      ...tdBase,
                      textAlign: col.align || 'left',
                      ...(ci === 0 ? { borderLeft: '3px solid var(--th-row-selected-border)', position: 'sticky', left: iconColsWidth, zIndex: 1, background: 'inherit' } : {}),
                    };
                    return (
                      <td key={col.key} colSpan={cellRender && col.editColSpan > 1 ? col.editColSpan : undefined} style={cellRender ? { ...tdStyle, padding: 2, overflow: 'visible' } : tdStyle}>
                        {cellRender ? cellRender(EMPTY_ADD_ROW) : null}
                      </td>
                    );
                  })}
                </tr>
                {renderAddSubRow && (
                  <tr style={{ background: 'var(--th-row-selected)' }}>
                    <td colSpan={columns.length + extraCols} style={{ padding: 0, borderLeft: '3px solid var(--th-row-selected-border)', borderBottom: '1px solid var(--th-border-light)' }}>
                      {renderAddSubRow(closeAdd, { offsetOf: expandedOffsetOf })}
                    </td>
                  </tr>
                )}
              </>
            );
          })()}

          {/* Add row — ghost trigger */}
          {onAdd && !addActive && (
            <tr onClick={() => setAddActive(true)} style={{ cursor: 'pointer' }}>
              <td
                colSpan={columns.length + extraCols}
                style={{ ...tdBase, borderLeft: 'var(--tg-node-inactive-border)', color: 'var(--th-border)', fontSize: 'var(--fs-small)' }}
              >
                + {addFormLabel || 'Add row'}
              </td>
            </tr>
          )}

          {/* Add row — active form. Mirrors the edit lifecycle: inputs in the row,
              then an Add/Cancel bar in a sub-row underneath (UX Consistency — the
              edit row has Save/Cancel there; Enter/Escape still work). */}
          {onAdd && addActive && (
            <>
            <tr style={{ background: 'var(--th-row-selected)' }}>
              {onCopy && <td style={{ ...tdBase, ...iconTdSticky(copyColLeft) }} />}
              {onDelete && <td style={{ ...tdBase, ...iconTdSticky(deleteColLeft) }} />}
              {onReorder && <td style={{ ...tdBase, ...iconTdSticky(reorderColLeft) }} />}
              {showEditCol && <td style={{ ...tdBase, ...iconTdSticky(editColLeft) }} />}
              {columns.map((col, ci) => {
                const tdStyle = {
                  ...tdBase,
                  padding: 2,
                  ...cellFont(col.textKind),
                  // The add row holds only controls, so its text (a checkbox word,
                  // a custom renderAddCell) takes the same edit font.
                  fontSize: 'var(--tg-cell-fs)',
                  ...(ci === 0 ? { borderLeft: '3px solid var(--th-accent)', position: 'sticky', left: iconColsWidth, zIndex: 1, background: 'inherit' } : {}),
                };
                return (
                  <td key={col.key} style={tdStyle}>
                    {col.renderAddCell ? col.renderAddCell({
                      fields: addFields,
                      set: (key, value) => setAddFields(f => ({ ...f, [key]: value })),
                      commit: commitAdd,
                      cancel: cancelAdd,
                    }) : col.addField && (
                      <input
                        placeholder={col.label}
                        value={addFields[col.key] || ''}
                        onChange={e => setAddFields(f => ({ ...f, [col.key]: e.target.value }))}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); commitAdd(); }
                          if (e.key === 'Escape') { cancelAdd(); }
                        }}
                        style={INPUT_STYLE}
                      />
                    )}
                  </td>
                );
              })}
            </tr>
            <tr style={{ background: 'var(--th-row-selected)' }}>
              <td colSpan={columns.length + extraCols} style={{ padding: 0, borderLeft: '3px solid var(--th-accent)', borderBottom: '1px solid var(--th-border-light)' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '5px 8px' }}>
                  <button
                    onClick={commitAdd}
                    style={BTN_PRIMARY}
                  >Add</button>
                  <button
                    onClick={cancelAdd}
                    style={BTN_SECONDARY}
                  >Cancel</button>
                  {addError ? (
                    <Text kind="warn">⚠ {addError}</Text>
                  ) : (
                    <Text kind="note">
                      New row — Add to save, Cancel to discard.
                    </Text>
                  )}
                </div>
              </td>
            </tr>
            </>
          )}
        </tbody>
        {footerRow && (
          <tfoot>
            <tr style={{ background: 'var(--th-row-even)', borderTop: '1px solid var(--th-border-light)', fontWeight: 600 }}>
              {onCopy && <td style={{ ...tdBase, ...iconTdSticky(copyColLeft), background: 'var(--th-row-even)' }} />}
              {onDelete && <td style={{ ...tdBase, ...iconTdSticky(deleteColLeft), background: 'var(--th-row-even)' }} />}
              {onReorder && <td style={{ ...tdBase, ...iconTdSticky(reorderColLeft), background: 'var(--th-row-even)' }} />}
              {showEditCol && <td style={{ ...tdBase, ...iconTdSticky(editColLeft), background: 'var(--th-row-even)' }} />}
              {columns.map((col, ci) => (
                <td key={col.key} style={{
                  ...tdBase,
                  textAlign: col.align || 'left',
                  ...(ci === 0 ? { borderLeft: 'var(--tg-node-inactive-border)', position: 'sticky', left: iconColsWidth, zIndex: 1, background: 'var(--th-row-even)' } : {}),
                }}>
                  {footerRow[col.key] ?? ''}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
      </div>
    </div>
  );
}
